import React, {useEffect, useMemo, useState} from 'react';
import {Box, createListCollection, Spinner, Text, VStack} from '@chakra-ui/react';
import {useChat} from '@/components/providers/ChatProvider';
import FullScreenChatUI from '@/components/ui/chat/FullScreenChatUI';
import ScenarioService from '@/services/agency/ScenarioService';
import ScenarioSelect from "@/pages/chat/ScenarioSelect";

const ChatPage = () => {
    const {startNewChat, openChat, closeChat, visibleChats, streamingState, sendMessage} = useChat();
    const [pageChatId, setPageChatId] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [scenariosLoading, setScenariosLoading] = useState(true);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [restoredChats, setRestoredChats] = useState([]);
    const [showMode, setShowMode] = useState(null);

    useEffect(() => {
        ScenarioService.getList()
            .then(res => setScenarios(res.items ?? []))
            .finally(() => setScenariosLoading(false));
    }, []);

    useEffect(() => {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('chat-'));
        const restorable = keys.map(key => {
            try {
                const data = JSON.parse(localStorage.getItem(key));
                const chatId = key.replace('chat-', '');
                return { chatId, ...data };
            } catch {
                return null;
            }
        }).filter(Boolean);
        setRestoredChats(restorable);
    }, []);

    useEffect(() => {
        if (!selectedScenario) return;

        if (pageChatId) {
            closeChat(pageChatId);
        }
        const newId = startNewChat(selectedScenario);
        setPageChatId(newId);

        return () => {
            closeChat(newId);
        };
    }, [selectedScenario]);

    const scenarioCollection = useMemo(() =>
            createListCollection({
                items: scenarios.map(s => ({value: s.id, label: s.name})),
            }),
        [scenarios]
    );

    const currentChat = visibleChats.find(chat => chat.id === pageChatId);

    const handleWhatsUpNo = () => {
        sendMessage({ type: 'text', text: 'No' }, currentChat.id);
    };

    if (scenariosLoading) {
        return (
            <VStack h="100vh" w="100vw" bg="bg.primary" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="white"/>
                <Text mt={4} color="white">Loading scenarios...</Text>
            </VStack>
        );
    }

    return (
        <Box position="relative" h="100vh" w="100vw" bg="bg.primary" overflow="hidden">
            <Box
                position="absolute"
                inset={0}
                filter={currentChat ? 'none' : 'blur(6px)'}
                pointerEvents={currentChat ? 'auto' : 'none'}
                transition={'filter 0.3s'}
            >
                <FullScreenChatUI
                    chat={currentChat}
                    onSendMessage={(message) => sendMessage(message, currentChat.id)}
                    streamingState={streamingState}
                    isSending={false}
                    selectedScenario={selectedScenario}
                    scenarioCollection={scenarioCollection}
                    scenarios={scenarios}
                    onScenarioSelected={setSelectedScenario}
                    onWhatsUpNo={handleWhatsUpNo}
                />
            </Box>

            {/* Overlay — shown when no active chat */}
            {!currentChat && (
                <Box
                    position="absolute"
                    inset={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="blackAlpha.500"
                    zIndex={20}
                >
                    {!showMode ? (
                        <Box
                            bg="gray.800"
                            borderRadius="xl"
                            p={8}
                            minW="360px"
                            boxShadow="2xl"
                        >
                            <Text color="white" fontSize="lg" fontWeight="semibold" mb={6}>
                                {restoredChats.length > 0 ? 'Choose an option' : 'Start a conversation'}
                            </Text>
                            <VStack gap={3}>
                                <button
                                    onClick={() => setShowMode('new')}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        background: '#4299e1',
                                        color: 'white',
                                        borderRadius: '6px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    + New conversation
                                </button>
                                {restoredChats.length > 0 && (
                                    <button
                                        onClick={() => setShowMode('restore')}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            background: '#48bb78',
                                            color: 'white',
                                            borderRadius: '6px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                        }}
                                    >
                                        ↻ Resume conversation ({restoredChats.length})
                                    </button>
                                )}
                            </VStack>
                        </Box>
                    ) : showMode === 'new' ? (
                        <Box
                            bg="gray.800"
                            borderRadius="xl"
                            p={8}
                            minW="360px"
                            boxShadow="2xl"
                        >
                            <Text color="white" fontSize="lg" fontWeight="semibold" mb={4}>
                                Select a scenario
                            </Text>
                            <ScenarioSelect
                                collection={scenarioCollection}
                                selectedScenario={selectedScenario}
                                scenarios={scenarios}
                                onScenarioChange={setSelectedScenario}
                            />
                            <button
                                onClick={() => setShowMode(null)}
                                style={{
                                    marginTop: '12px',
                                    width: '100%',
                                    padding: '8px',
                                    background: 'transparent',
                                    color: '#a0aec0',
                                    border: '1px solid #4a5568',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                Back
                            </button>
                        </Box>
                    ) : showMode === 'restore' ? (
                        <Box
                            bg="gray.800"
                            borderRadius="xl"
                            p={8}
                            minW="360px"
                            maxH="600px"
                            overflowY="auto"
                            boxShadow="2xl"
                        >
                            <Text color="white" fontSize="lg" fontWeight="semibold" mb={4}>
                                Conversation history
                            </Text>
                            <VStack gap={2} align="stretch">
                                {restoredChats.map(chat => (
                                    <button
                                        key={chat.chatId}
                                        onClick={() => {
                                            openChat(chat.chatId);
                                            setPageChatId(chat.chatId);
                                            setShowMode(null);
                                        }}
                                        style={{
                                            padding: '12px',
                                            background: '#2d3748',
                                            color: 'white',
                                            border: '1px solid #4a5568',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: '#cbd5e0' }}>
                                            {new Date(chat.createdAt).toLocaleString()}
                                        </div>
                                        <div>{chat.chatId.slice(0, 8)}...</div>
                                    </button>
                                ))}
                            </VStack>
                            <button
                                onClick={() => setShowMode(null)}
                                style={{
                                    marginTop: '12px',
                                    width: '100%',
                                    padding: '8px',
                                    background: 'transparent',
                                    color: '#a0aec0',
                                    border: '1px solid #4a5568',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                }}
                            >
                                Back
                            </button>
                        </Box>
                    ) : selectedScenario ? (
                        <VStack>
                            <Spinner size="xl" color="white"/>
                            <Text mt={4} color="white">Starting chat...</Text>
                        </VStack>
                    ) : null}
                </Box>
            )}
        </Box>
    );
};

export default ChatPage;
